import React, { useState, useEffect } from 'react';
import {
  AppBar, Toolbar, Button, Box, IconButton, Drawer, List, ListItem, ListItemText, useMediaQuery, Menu, MenuItem
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LanguageIcon from '@mui/icons-material/Language';
import { useTheme } from '@mui/material/styles';
import langIcon from '../imaged/lang.png';
import logo from '../imaged/logo.jpeg';
// Text brand for Paritranam; replace with logo if provided
import { NavLink, useNavigate } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';

const navLinks = [
  { key: 'nav.products', to: '/products' },
  { key: 'nav.gallery', to: '/gallery' },
  { key: 'nav.contact', to: '/contact' },
];

const noop = () => {};

// ... (rest of imports)

const Header = () => {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [aboutEl, setAboutEl] = useState(null);
  const { t, language, setLanguage } = useLanguage();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const [isScrolled, setIsScrolled] = useState(false);

  const handleLanguageMenu = (event) => setAnchorEl(event.currentTarget);
  const handleLanguageClose = () => setAnchorEl(null);
  const changeLang = (lang) => { setLanguage(lang); handleLanguageClose(); };
  const openAbout = (e)=> setAboutEl(e.currentTarget);
  const closeAbout = ()=> setAboutEl(null);

  return (
    <AppBar
      position="fixed"
      color="transparent"
      elevation={0}
      sx={{
        py: { xs: 0, md: 1 },
        borderBottom: '1px solid',
        borderColor: 'rgba(0,0,0,0.05)',
        bgcolor: 'rgba(255,255,255,0.8)',
        backdropFilter: 'blur(10px)',
        zIndex: 1200,
        transition: 'all 0.3s ease-in-out',
      }}
    >
      <Toolbar sx={{ 
        justifyContent: 'space-between', 
        px: { xs: 2, sm: 3, md: 4 }, 
        minHeight: { xs: 40, md: 64 },
        transition: 'all 0.3s ease-in-out',
      }}>
        {/* Brand */}
        <Box component={NavLink} to="/home" sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
          <Box component="img" src={logo} alt="Paritranam" sx={{ height: { xs: 34, md: 50 }, mr: 1, borderRadius: '50%' }} />
          <Box sx={{ fontWeight: 900, fontSize: { xs: '0.95rem', md: '1.25rem' }, color: 'primary.dark', letterSpacing: { xs: 1.2, md: 2 } }}>
            {t('header.brand')}
          </Box>
        </Box>

        {isMdUp ? (
          <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
            <Button
              component={NavLink}
              to="/home"
              color="inherit"
              sx={{
                fontWeight: 700,
                fontSize: '0.85rem',
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                color: 'grey.800',
                px: 1,
                minWidth: 'auto',
                transition: 'all 0.3s ease-in-out',
                '&.active': { color: 'primary.main' }
              }}
            >
              {t('nav.home')}
            </Button>
            {/* About Us dropdown */}
            <Button
              color="inherit"
              onClick={openAbout}
              sx={{
                fontWeight: 700,
                fontSize: '0.85rem',
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                color: 'grey.800',
                px: 1,
                minWidth: 'auto'
              }}
            >
              {t('nav.aboutUs')}
            </Button>
            <Menu anchorEl={aboutEl} open={Boolean(aboutEl)} onClose={closeAbout}>
              <MenuItem onClick={()=>{ closeAbout(); navigate('/about-ayurveda'); }}>{t('nav.aboutAyurveda')}</MenuItem>
              <MenuItem onClick={()=>{ closeAbout(); navigate('/about-paritranam'); }}>{t('nav.aboutParitranam')}</MenuItem>
            </Menu>
            {navLinks.map((link) => (
              <Button
                key={link.key}
                component={NavLink}
                to={link.to}
                color="inherit"
                sx={{
                  fontWeight: 700,
                  fontSize: '0.85rem',
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  color: 'grey.800',
                  px: 1,
                  minWidth: 'auto',
                  transition: 'all 0.3s ease-in-out',
                  '&.active': { color: 'primary.main' }
                }}
              >
                {t(link.key)}
              </Button>
            ))}
            <IconButton
              onClick={handleLanguageMenu}
              color="inherit"
              sx={{ ml: 1 }}
            >
              <Box 
                component="img" 
                src={langIcon} 
                alt="Language" 
                sx={{ 
                  width: { xs: 28, md: 40 }, 
                  height: { xs: 28, md: 40 },
                  filter: 'drop-shadow(0px 2px 4px rgba(0,0,0,0.3))',
                  transition: 'transform 0.2s',
                  '&:hover': { transform: 'scale(1.1)' }
                }} 
              />
            </IconButton>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleLanguageClose}>
              <MenuItem onClick={() => changeLang('en')}>English</MenuItem>
              <MenuItem onClick={() => changeLang('mr')}>मराठी</MenuItem>
              <MenuItem onClick={() => changeLang('hi')}>हिन्दी</MenuItem>
            </Menu>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton onClick={handleLanguageMenu} color="inherit">
              <Box 
                component="img" 
                src={langIcon} 
                alt="Language" 
                sx={{ 
                  width: 36, 
                  height: 36,
                  filter: 'drop-shadow(0px 2px 4px rgba(0,0,0,0.3))'
                }} 
              />
            </IconButton>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleLanguageClose}>
              <MenuItem onClick={() => changeLang('en')}>English</MenuItem>
              <MenuItem onClick={() => changeLang('mr')}>मराठी</MenuItem>
              <MenuItem onClick={() => changeLang('hi')}>हिन्दी</MenuItem>
            </Menu>
          </Box>
        )}
      </Toolbar>

      {/* Mobile Navigation Bar */}
      {!isMdUp && (
        <Box sx={{ 
          display: 'flex', 
          overflowX: 'auto', 
          whiteSpace: 'nowrap',
          bgcolor: 'rgba(255,255,255,0.95)',
          pb: 0,
          pt: 0,
          px: 0.5,
          gap: 0.75,
          borderTop: '1px solid rgba(0,0,0,0.05)',
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}>
          <Button
            component={NavLink}
            to="/home"
            sx={{
              minWidth: 'auto',
              color: 'text.secondary',
              fontWeight: 600,
              fontSize: '0.8rem',
              textTransform: 'none',
              p: 0.2,
              flexShrink: 0,
              '&.active': {
                color: 'primary.main',
                borderBottom: '2px solid',
                borderColor: 'primary.main',
                borderRadius: 0
              }
            }}
          >
            {t('nav.home')}
          </Button>
          {/* Mobile horizontal menu with About Us dropdown */}
          <Button
            onClick={openAbout}
            sx={{
              minWidth: 'auto',
              color: 'text.secondary',
              fontWeight: 600,
              fontSize: '0.8rem',
              textTransform: 'none',
              p: 0.2,
              flexShrink: 0
            }}
          >
            {t('nav.aboutUs')}
          </Button>
          <Menu anchorEl={aboutEl} open={Boolean(aboutEl)} onClose={closeAbout}>
            <MenuItem onClick={()=>{ closeAbout(); navigate('/about-ayurveda'); }}>{t('nav.aboutAyurveda')}</MenuItem>
            <MenuItem onClick={()=>{ closeAbout(); navigate('/about-paritranam'); }}>{t('nav.aboutParitranam')}</MenuItem>
          </Menu>
          {navLinks.map((link) => (
            <Button
              key={link.key}
              component={NavLink}
              to={link.to}
              sx={{
                minWidth: 'auto',
                color: 'text.secondary',
                fontWeight: 600,
                fontSize: '0.8rem',
                textTransform: 'none',
                p: 0.2,
                flexShrink: 0,
                '&.active': {
                  color: 'primary.main',
                  borderBottom: '2px solid',
                  borderColor: 'primary.main',
                  borderRadius: 0
                }
              }}
            >
              {t(link.key)}
            </Button>
          ))}
        </Box>
      )}

      {/* Moving tagline bar */}
      <Box
        sx={{
          bgcolor: 'grey.100',
          borderTop: '1px solid',
          borderColor: 'grey.200',
          overflow: 'hidden',
          py: { xs: 0.25, md: 1 },
          display: { xs: 'block', md: 'block' },
          minHeight: { xs: 28, md: 'auto' }
        }}
      >
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: { xs: 4, md: 8 },
            whiteSpace: 'nowrap',
            px: { xs: 0.75, md: 2 },
            '@keyframes marquee': {
              '0%': { transform: 'translateX(100%)' },
              '100%': { transform: 'translateX(-100%)' }
            },
            animation: { xs: 'marquee 22s linear infinite', md: 'marquee 18s linear infinite' }
          }}
        >
          <Box
            component="span"
            sx={{
              color: 'text.secondary',
              fontWeight: 700,
              letterSpacing: 0.3,
              fontSize: { xs: '0.8rem', md: '1rem' }
            }}
          >
            {t('home.title')}
          </Box>
          <Box
            component="span"
            sx={{
              color: 'text.secondary',
              fontWeight: 700,
              letterSpacing: 0.3,
              fontSize: { xs: '0.8rem', md: '1rem' }
            }}
          >
            {t('home.title')}
          </Box>
        </Box>
      </Box>
    </AppBar>
  );
};

export default Header;
